import { createFileRoute, notFound } from '@tanstack/react-router';

/**
 * Every address that is not a page.
 *
 * ===========================================================================
 * Why this route exists
 * ===========================================================================
 * TanStack Router renders the root `notFoundComponent` for an unmatched path
 * on its own, but leaves the response at 200 — a crawler that follows a stale
 * `/blog/old-slug`, a mistyped link, or (as Search Console found) a legacy
 * `/w/<id>` page from the site this one replaced, gets "ReviewsJet" at 200
 * OK, and every one of those URLs reads as a distinct, real, *duplicate* page
 * rather than as the miss it is.
 *
 * This splat exists to make that miss honest: `throw notFound()` in a loader
 * is the one mechanism in this app confirmed to set a genuine HTTP 404 — it is
 * the same call `widgets.$slug.tsx` and `f.$slug.tsx` use for an unknown slug,
 * and, unlike them, this route has no real case to fall through to, so it
 * always throws.
 *
 * ===========================================================================
 * A previous version of this file set the status by hand, and didn't
 * ===========================================================================
 * It called `setResponseStatus(404)` from inside a `createIsomorphicFn`
 * server branch, behind a dynamic `import('@tanstack/react-start/server')` —
 * and shipped a real HTTP 200 in production regardless, confirmed by curling
 * a dead `/w/` URL. The dynamic import makes that call async; the guess is
 * that the document's status line is already flushed by the time it resolves.
 * `notFound()` has no such race — it is thrown synchronously, before the
 * router renders anything — and it is proven correct here because the same
 * app already relies on it elsewhere.
 *
 * The trade is the custom "Page not found" head this route used to set:
 * `notFound()` bypasses this route's own `head`, same as it does for a bad
 * widget slug, so the tab title falls back to the root's default. That is the
 * existing, accepted behaviour for every other `notFound()` in this app, and
 * an HTTP 404 is what actually keeps a URL out of the index — Google does not
 * need a `noindex` tag on a page it already can't fetch successfully.
 */
export const Route = createFileRoute('/$')({
  loader: () => {
    throw notFound();
  },
});
